import React from "react";
import { X, Loader2, PlaneTakeoff } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useCurrency } from "../../context/CurrencyContext";
import { useMember } from "../../context/MemberAuthContext";
import CurrencySelector from "./CurrencySelector";
import PaystackButton from "./PaystackButton";

const inputCls = "w-full rounded-lg bg-white border border-parchment px-3.5 py-2.5 text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-forest";
const labelCls = "block text-sm font-medium text-ink mb-1.5";

const GENDERS = ["", "Female", "Male", "Other", "Prefer not to say"];
const PLANS = ["Pay in full", "Deposit now, balance later", "Instalments (monthly)"];
const HEARD = ["", "Social media", "Friend / family", "WhatsApp group", "Email newsletter", "Event / church", "Website", "Other"];

export default function EventRegistrationModal({ event, onClose }) {
  const fee = Number(event.registration_fee || 0);
  const feeLabel = event.fee_label || "Registration Fee";
  const { format, isConverted } = useCurrency();
  const [f, setF] = React.useState({
    trip: event.title || "",
    full_name: "", date_of_birth: "", gender: "", country: "", email: "", phone: "", emergency_contact: "",
    reg_type: "individual", headcount: 1, traveller_names: "",
    needs_hotel: false, needs_transport: false, passport_note: "", dietary_medical: "",
    outfit: "", heard_from: "", payment_plan: "Pay in full", message: "",
  });
  const [busy, setBusy] = React.useState("");
  const { member } = useMember();
  React.useEffect(() => {
    if (!member) return;
    setF((p) => ({ ...p, email: p.email || member.email || "", full_name: p.full_name || member.name || "" }));
  }, [member]);
  const [ppReady, setPpReady] = React.useState(false);
  const paypalRef = React.useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email);
  const valid = f.full_name.trim() && emailOk;

  const buildPayload = (method) => ({
    ...f,
    headcount: Number(f.headcount) || 1,
    traveller_names: f.reg_type === "group" ? f.traveller_names.split("\n").map((s) => s.trim()).filter(Boolean) : [],
    origin_url: window.location.origin,
    method,
  });

  React.useEffect(() => {
    if (fee <= 0) return;
    api.get("/paypal/config").then(({ data }) => {
      if (!data.configured || !data.client_id) return;
      if (window.paypal) { setPpReady(true); return; }
      const s = document.createElement("script");
      s.src = `https://www.paypal.com/sdk/js?client-id=${data.client_id}&currency=USD`;
      s.onload = () => setPpReady(true);
      document.body.appendChild(s);
    }).catch(() => {});
  }, [fee]);

  React.useEffect(() => {
    if (fee <= 0 || !ppReady || !window.paypal || !paypalRef.current) return;
    paypalRef.current.innerHTML = "";
    window.paypal.Buttons({
      style: { color: "gold", shape: "pill", label: "pay", height: 45 },
      onClick: (data, actions) => {
        if (!valid) { toast.error("Please fill in your name and a valid email first."); return actions.reject(); }
        return actions.resolve();
      },
      createOrder: async () => {
        const { data } = await api.post(`/events/${event.id}/register`, buildPayload("paypal"));
        return data.paypal_order_id;
      },
      onApprove: async (data) => {
        const { data: res } = await api.post(`/events/registration-paypal-capture/${data.orderID}`);
        if (res.payment_status === "paid") window.location.href = "/events/register/success?paid=paypal";
        else toast.error("Payment could not be completed.");
      },
    }).render(paypalRef.current);
  }, [ppReady, valid, f]); // eslint-disable-line

  const submitStripeOrFree = async () => {
    if (!valid) { toast.error("Please fill in your name and a valid email."); return; }
    setBusy("go");
    try {
      const { data } = await api.post(`/events/${event.id}/register`, buildPayload("stripe"));
      if (data.method === "free" || data.status === "registered") {
        toast.success("You're registered! We'll be in touch shortly.");
        window.location.href = "/events/register/success?free=1";
        return;
      }
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit registration.");
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-center items-start overflow-y-auto py-8 px-4" data-testid="event-registration-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl rounded-2xl border border-parchment bg-cream shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-cream border-b border-parchment px-6 py-4 flex items-start justify-between rounded-t-2xl">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs text-gold inline-flex items-center gap-1.5"><PlaneTakeoff className="w-4 h-4" /> Trip Registration</p>
            <h3 className="font-serif text-2xl text-ink leading-tight mt-1">{event.title}</h3>
          </div>
          <button onClick={onClose} data-testid="event-registration-close" className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {event.registration_note && <p className="text-sm text-ink-muted bg-gold/10 border border-gold/30 rounded-lg px-4 py-3">{event.registration_note}</p>}

          <div><label className={labelCls}>Which trip are you signing up for?</label><input value={f.trip} onChange={(e) => set("trip", e.target.value)} data-testid="reg-trip-input" className={inputCls} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Full name *</label><input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="reg-fullname-input" className={inputCls} /></div>
            <div><label className={labelCls}>Email *</label><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email-input" className={inputCls} /></div>
            <div><label className={labelCls}>Date of birth</label><input type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="reg-dob-input" className={inputCls} /></div>
            <div><label className={labelCls}>Gender</label><select value={f.gender} onChange={(e) => set("gender", e.target.value)} data-testid="reg-gender-input" className={inputCls}>{GENDERS.map((g) => <option key={g} value={g}>{g || "Select…"}</option>)}</select></div>
            <div><label className={labelCls}>Country based in</label><input value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="reg-country-input" className={inputCls} /></div>
            <div><label className={labelCls}>Phone</label><input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="reg-phone-input" className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Emergency contact (name & phone)</label><input value={f.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} data-testid="reg-emergency-input" className={inputCls} /></div>

          <div>
            <label className={labelCls}>Are you registering as an individual or a group?</label>
            <div className="flex gap-3">
              {["individual", "group"].map((t) => (
                <button key={t} type="button" onClick={() => set("reg_type", t)} data-testid={`reg-type-${t}`} className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold capitalize transition-colors ${f.reg_type === t ? "bg-forest text-cream border-forest" : "border-parchment text-ink hover:border-forest"}`}>{t}</button>
              ))}
            </div>
          </div>
          {f.reg_type === "group" && (
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>No. of travellers</label><input type="number" min="1" value={f.headcount} onChange={(e) => set("headcount", e.target.value)} data-testid="reg-headcount-input" className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Traveller names (one per line)</label><textarea rows={3} value={f.traveller_names} onChange={(e) => set("traveller_names", e.target.value)} data-testid="reg-travellers-input" className={inputCls} /></div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-ink bg-white border border-parchment rounded-lg px-4 py-3"><input type="checkbox" checked={f.needs_hotel} onChange={(e) => set("needs_hotel", e.target.checked)} data-testid="reg-hotel-checkbox" className="w-4 h-4 accent-forest" /> I need hotel / accommodation</label>
            <label className="flex items-center gap-2 text-sm text-ink bg-white border border-parchment rounded-lg px-4 py-3"><input type="checkbox" checked={f.needs_transport} onChange={(e) => set("needs_transport", e.target.checked)} data-testid="reg-transport-checkbox" className="w-4 h-4 accent-forest" /> I need transport / airport pickup</label>
          </div>
          <div><label className={labelCls}>Passport / travel document details (international trips)</label><input value={f.passport_note} onChange={(e) => set("passport_note", e.target.value)} placeholder="Passport no., nationality, expiry — or 'will provide later'" data-testid="reg-passport-input" className={inputCls} /></div>
          <div><label className={labelCls}>Dietary / medical needs</label><textarea rows={2} value={f.dietary_medical} onChange={(e) => set("dietary_medical", e.target.value)} data-testid="reg-dietary-input" className={inputCls} /></div>
          <div><label className={labelCls}>Which outfit / Yorùbá attire would you like us to make for you? (style, not fabric)</label><input value={f.outfit} onChange={(e) => set("outfit", e.target.value)} data-testid="reg-outfit-input" className={inputCls} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>How did you find out about the trip?</label><select value={f.heard_from} onChange={(e) => set("heard_from", e.target.value)} data-testid="reg-heard-input" className={inputCls}>{HEARD.map((h) => <option key={h} value={h}>{h || "Select…"}</option>)}</select></div>
            <div><label className={labelCls}>Preferred payment plan</label><select value={f.payment_plan} onChange={(e) => set("payment_plan", e.target.value)} data-testid="reg-plan-input" className={inputCls}>{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
          </div>
          <div><label className={labelCls}>Anything else? (message)</label><textarea rows={2} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="reg-message-input" className={inputCls} /></div>

          {/* Payment */}
          <div className="border-t border-parchment pt-4">
            {fee > 0 ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm text-ink-muted">{feeLabel}</p>
                    <p className="font-serif text-2xl text-forest" data-testid="reg-fee">{format(fee)}</p>
                    {isConverted && <p className="text-xs text-forest">Charged as ${fee.toFixed(2)} USD at today's rate.</p>}
                  </div>
                  <CurrencySelector variant="light" />
                </div>
                <button onClick={submitStripeOrFree} disabled={busy !== ""} data-testid="reg-stripe-button" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-night font-semibold py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Register & Pay with Card`}
                </button>
                <div className="text-center text-xs text-ink-muted my-2">or</div>
                <div ref={paypalRef} data-testid="reg-paypal-buttons" />
                <PaystackButton
                  variant="light"
                  initialize={async (cur) => {
                    if (!valid) { toast.error("Please fill in your name and a valid email first."); throw new Error("invalid"); }
                    const { data } = await api.post(`/events/${event.id}/register`, { ...buildPayload("paystack"), currency: cur });
                    return data.authorization_url;
                  }}
                />
              </>
            ) : (
              <button onClick={submitStripeOrFree} disabled={busy !== ""} data-testid="reg-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream font-semibold py-3 rounded-full hover:bg-forest-light transition-colors disabled:opacity-60">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit Registration"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
